import React from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { hardRedirect } from './utils/navigation';
import { AuthProvider } from './contexts/AuthContext';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import TopBar from './components/ui/TopBar';
import BottomTabBar from './components/ui/BottomTabBar';
import PageTransition from './components/ui/PageTransition';
import PrivateRoute from './components/PrivateRoute';

// Page imports
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AccountTypeSelection from './pages/AccountTypeSelection';
import EventDetail from './pages/EventDetail';
import CreateEvent from './pages/CreateEvent';
import EditEvent from './pages/EditEvent';
import EditProfile from './pages/EditProfile';
import TestCategories from './pages/TestCategories';
import MyPage from './pages/MyPage';
import JoinPage from './pages/JoinPage';
import SocialPage from './pages/SocialPage';
import SettingsPage from './pages/SettingsPage';
import CreateGroupCliqq from './pages/CreateGroupCliqq';
import AddToGroupCliqq from './pages/AddToGroupCliqq';
import CreateCliqqType from './pages/CreateCliqqType';
import UserProfile from './pages/UserProfile';
import NotificationsPage from './pages/NotificationsPage';
import MessagesPage from './pages/MessagesPage';
import PromotionPage from './pages/PromotionPage';
import BusinessDashboard from './pages/BusinessDashboard';
import BusinessView from './pages/BusinessView';
import NotFound from './pages/NotFound';

// Use hash-based routing inside the Capacitor native WebView (no history API /
// server behind it); use clean browser URLs on the web build.
const Router = Capacitor.isNativePlatform() ? HashRouter : BrowserRouter;

/**
 * AppLayout wraps authenticated / standard pages with TopBar,
 * BottomTabBar, and the shared content container.
 */
function AppLayout() {
  return (
    <>
      {/* Accessibility: skip-to-content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-500 focus:text-white focus:rounded-xl focus:text-body-sm focus:font-semibold focus:shadow-soft-md"
      >
        Skip to content
      </a>

      <TopBar />

      <main id="main-content" className="pb-20 md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <Outlet />
        </div>
      </main>

      <BottomTabBar />
    </>
  );
}

/**
 * AnimatedRoutes renders all application routes inside AnimatePresence
 * so page transitions animate correctly on route changes.
 */
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* ---------- Full-screen auth pages (no AppLayout) ---------- */}
        <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
        <Route path="/register" element={<PageTransition><Register /></PageTransition>} />
        <Route path="/forgot-password" element={<PageTransition><ForgotPassword /></PageTransition>} />
        <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />

        {/* ---------- Pages wrapped in AppLayout ---------- */}
        <Route element={<AppLayout />}>
          <Route
            path="/"
            element={<PageTransition><Home /></PageTransition>}
          />
          <Route
            path="/account-type"
            element={<PageTransition><AccountTypeSelection /></PageTransition>}
          />
          <Route
            path="/events/:id"
            element={<PageTransition><EventDetail /></PageTransition>}
          />
          <Route
            path="/events/:id/edit"
            element={<PageTransition><PrivateRoute><EditEvent /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/profile/:id"
            element={<PageTransition><UserProfile /></PageTransition>}
          />
          <Route
            path="/create-event"
            element={<PageTransition><PrivateRoute><CreateEvent /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/edit-profile"
            element={<PageTransition><PrivateRoute><EditProfile /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/test-categories"
            element={<PageTransition><TestCategories /></PageTransition>}
          />
          <Route
            path="/my-page"
            element={<PageTransition><PrivateRoute><MyPage /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/create-cliqq"
            element={<PageTransition><PrivateRoute><CreateCliqqType /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/join"
            element={<PageTransition><PrivateRoute><JoinPage /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/social"
            element={<PageTransition><PrivateRoute><SocialPage /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/settings"
            element={<PageTransition><PrivateRoute><SettingsPage /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/create-group-cliqq"
            element={<PageTransition><PrivateRoute><CreateGroupCliqq /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/add-to-group-cliqq"
            element={<PageTransition><PrivateRoute><AddToGroupCliqq /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/notifications"
            element={<PageTransition><PrivateRoute><NotificationsPage /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/messages"
            element={<PageTransition><PrivateRoute><MessagesPage /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/promotions"
            element={<PageTransition><PromotionPage /></PageTransition>}
          />
          <Route
            path="/business/dashboard"
            element={<PageTransition><PrivateRoute><BusinessDashboard /></PrivateRoute></PageTransition>}
          />
          <Route
            path="/business/:id"
            element={<PageTransition><BusinessView /></PageTransition>}
          />

          {/* Catch-all 404 */}
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-surface-50">
      {/* Mounted here rather than inside AppLayout: /login, /register,
          /forgot-password and /reset-password render OUTSIDE the layout, so a
          Toaster inside it meant every toast on those pages -- including the
          only feedback a failed registration produces -- rendered nowhere. */}
      <Toaster position="top-right" richColors />
      <ErrorBoundary showReportOption={true}>
        <AuthProvider>
          <Router>
            <ErrorBoundary
              fallback={(error, retry) => (
                <div className="max-w-2xl mx-auto p-6">
                  <div className="bg-error-50 border border-error-400 rounded-2xl p-6">
                    <div className="flex items-center mb-4">
                      <svg
                        className="h-6 w-6 text-error-400 mr-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                      <h3 className="text-lg font-medium text-error-600">
                        Something went wrong
                      </h3>
                    </div>
                    <p className="text-error-500 mb-4 text-body">
                      This page encountered an error and could not load properly.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={retry}
                        className="px-4 py-2 bg-error-500 text-white rounded-xl text-body-sm font-semibold hover:bg-error-600 transition-colors"
                      >
                        Try Again
                      </button>
                      <button
                        onClick={() => hardRedirect('/')}
                        className="px-4 py-2 bg-surface-200 text-surface-800 rounded-xl text-body-sm font-semibold hover:bg-surface-300 transition-colors"
                      >
                        Go Home
                      </button>
                    </div>
                  </div>
                </div>
              )}
            >
              <AnimatedRoutes />
            </ErrorBoundary>
          </Router>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
